import { computed } from 'vue'

import { useRoute } from 'nuxt/app'

import { useI18n } from 'vue-i18n'

import { useQueryClient } from '@tanstack/vue-query'

import { useLocation } from '@common/location-layer/composables/useLocation'
import { resolveCpBreadcrumbRegistryKey } from '@common/navigation-layer/config/cp-breadcrumb-registry'
import { isCourseProductRouteKey } from '@common/navigation-layer/utils/course-route-classification'
import { resolveCourseRouteGroupSlug } from '@common/navigation-layer/utils/resolve-course-route-group-slug'
import { resolveGroupNameFromCache } from '@communities/groups-layer/utils/resolve-group-name-from-cache'
import { useGroups } from '@communities/groups-layer/composables/useGroups'
import { useProductQueryStore } from '@courses/product-theme-layer/stores/product.query-store'
import type { CpCoursesProductBreadcrumbContext } from '@common/navigation-layer/types/cp-breadcrumb'

import type { LibraryProducts } from '@courses/types-layer/types/library'
import type { IProductDetail } from '@courses/types-layer/types/product'

function resolveCourseTitleFromCache(queryClient: ReturnType<typeof useQueryClient>, locationId: string, productId: string): string | null {
  if (process.server || !locationId || !productId) return null

  const library = queryClient.getQueryData(['library', locationId, 'list']) as LibraryProducts | undefined
  const libraryMatch = library?.find(product => product.id === productId)
  const libraryTitle = libraryMatch?.title?.trim()
  if (libraryTitle && libraryTitle.length > 0) return libraryTitle

  const product = queryClient.getQueryData(['product', locationId, 'detail', productId]) as IProductDetail | undefined
  const productTitle = product?.title?.trim()
  if (productTitle && productTitle.length > 0) return productTitle

  return null
}

/**
 * Supplies product id, resolved course title, optional community context, and group name for `useCPBreadcrumbs`
 * on course product, lesson, Live Sessions, purchase, and checkout routes.
 */
export function useCPCoursesProductBreadcrumbContext() {
  const route = useRoute()
  const { t } = useI18n()
  const { locationId } = useLocation()
  const queryStore = useProductQueryStore(locationId)
  const queryClient = useQueryClient()
  const { store: groupsStore, fetchGroupBySlug } = useGroups()

  const productIdForBreadcrumb = computed(() => {
    const key = resolveCpBreadcrumbRegistryKey(route)
    if (!isCourseProductRouteKey(key)) return ''
    const pid = route.params.productId
    return typeof pid === 'string' ? pid : ''
  })

  const groupSlugForBreadcrumb = computed(() => {
    if (!productIdForBreadcrumb.value) return ''
    return resolveCourseRouteGroupSlug(route)
  })

  const productQuery = queryStore.fetchProductQuery(productIdForBreadcrumb)
  const groupBySlugQuery = fetchGroupBySlug(groupSlugForBreadcrumb)

  return computed((): CpCoursesProductBreadcrumbContext | null => {
    const pid = productIdForBreadcrumb.value
    if (!pid) return null

    const locId = locationId.value
    const queryTitle = productQuery.data.value?.title?.trim()
    let courseName: string | null = null
    if (queryTitle && queryTitle.length > 0) {
      courseName = queryTitle
    } else if (!process.server) {
      courseName = resolveCourseTitleFromCache(queryClient, locId, pid)
    }
    if (!courseName) {
      courseName = t('common.loading')
    }

    const groupSlug = groupSlugForBreadcrumb.value
    const fetchedGroupName = groupBySlugQuery.data.value?.groupDetail?.name?.trim()
    let groupName: string | undefined
    if (groupSlug) {
      const current = groupsStore.currentGroup
      if (current?.slug === groupSlug) {
        const storeName = current.name?.trim()
        if (storeName && storeName.length > 0) {
          groupName = storeName
        }
      }

      if (!groupName && fetchedGroupName && fetchedGroupName.length > 0) {
        groupName = fetchedGroupName
      }

      if (!groupName && !process.server) {
        groupName = resolveGroupNameFromCache(queryClient, groupSlug)?.name
      }

      if (!groupName) {
        groupName = t('common.loading')
      }
    }

    return {
      productId: pid,
      courseName,
      groupSlug: groupSlug || undefined,
      groupName,
    }
  })
}
